import type {
  InvoiceContactDetailsDto,
  InvoiceContactMarketPartyDetailsRequest,
  MarketPartyOrSubUnitResponse,
  MarketPartyOrSubUnitResponseListResponse,
  MarketRoleType,
} from '../generated/model';

export const createMarketParty = (
  overrides: Partial<MarketPartyOrSubUnitResponse> = {},
): MarketPartyOrSubUnitResponse => ({
  accountType: 'SubUnit',
  ean13: '1234567890123',
  id: '2234567890123',
  mprIdMarketParty: '123',
  mprIdSubUnit: '441100',
  name: 'Test Market Party Eneco',
  ...overrides,
});

export const createInvoiceMarketParties = (
  overrides: Partial<MarketPartyOrSubUnitResponseListResponse> = {},
): MarketPartyOrSubUnitResponseListResponse => ({
  items: [
    createMarketParty(),
    createMarketParty({
      accountType: 'SubUnit',
      ean13: '123457891111',
      mprIdMarketParty: '456',
      mprIdSubUnit: '340022',
      name: 'Test Market Party Liander',
    }),
    createMarketParty({
      accountType: 'MarketParticipant',
      ean13: '1234567892222',
      mprIdMarketParty: '789',
      mprIdSubUnit: '550044',
      name: 'Octo Energy B.V.',
    }),
    createMarketParty({
      accountType: 'SubUnit',
      ean13: '1234567893333',
      mprIdMarketParty: '025',
      mprIdSubUnit: '020502',
      name: 'Test Market Party Dextro',
    }),
    createMarketParty({
      accountType: 'MarketParticipant',
      ean13: '1234567894444',
      mprIdMarketParty: '026',
      mprIdSubUnit: '990033',
      name: 'Volt Power',
    }),
  ],
  totalCount: 4,
  ...overrides,
});

export const createInvoiceContactDetails = (
  _mprId: string,
  request: InvoiceContactMarketPartyDetailsRequest,
  overrides: Partial<InvoiceContactDetailsDto> = {},
): InvoiceContactDetailsDto[] => {
  const invoiceContacts: Record<string, InvoiceContactDetailsDto> = {
    '020502': {
      accountId: '1234567890123',
      addressExtensionDescription: null,
      chamberOfCommerceNumber: '44BA85943',
      city: 'Utrecht',
      country: 'Nederland',
      ean13: '1234567893333',
      electronicAddressPrimary: 'contact@dextro.nl',
      houseNumber: 2,
      marketRole: 'DDQ' as MarketRoleType,
      mprId: '020502',
      registrationNumberOrigin: 'NL',
      statutoryName: 'Dextro B.V.',
      streetName: 'Utrechtseweg',
      subUnits: [
        {
          eanCode: '8738475983084',
          name: 'Dextro groen gas',
        },
        {
          eanCode: '872839475948574',
          name: 'Dextro elektra blauw',
        },
      ],
      subjectDescription: 'Verrekenen meetfouten',
      zipCode: '0205 MY',
    },
    '340022': {
      addressExtensionDescription: 'Lia van Liander',
      city: 'Arnhem',
      country: 'Nederland',
      electronicAddressPrimary: 'contact@liander.nl',
      houseNumber: 200,
      houseNumberExtension: 'B',
      marketRole: 'CTO' as MarketRoleType,
      mprId: '340022',
      registrationNumberOrigin: 'NL',
      statutoryName: 'Liander N.V.',
      streetName: 'Utrechtseweg',
      subjectDescription: 'Liander kosten',
      zipCode: '6812 AR',
    },
    '441100': {
      addressExtensionDescription: 'Ennie & Eneco',
      city: 'Rotterdam',
      country: 'Nederland',
      electronicAddressPrimary: 'contact@eneco.com',
      houseNumber: 100,
      houseNumberExtension: 'A',
      marketRole: 'DDQ' as MarketRoleType,
      mprId: '441100',
      registrationNumberOrigin: 'NL',
      statutoryName: 'Eneco B.V.',
      streetName: 'Eneco Straat',
      subjectDescription: 'Verrekenen meetfouten',
      zipCode: '3015 KK',
    },

    '550044': {
      addressExtensionDescription: 'Henry Smith',
      city: 'New Orleans',
      country: '',
      ean13: '8725364785930',
      electronicAddressPrimary: 'info@financial.com',
      houseNumber: 100,
      houseNumberExtension: 'A',
      marketRole: 'CSP' as MarketRoleType,
      mprId: '550044',
      registrationNumberOrigin: 'US',
      statutoryName: 'Octo Energy',
      streetName: 'Energy street',
      subUnits: [
        {
          eanCode: '8738475983084',
          name: 'Octa Elektricity Big-time',
        },
        {
          eanCode: '872839475948574',
          name: 'Octo Gas Small',
        },
      ],
      subjectDescription: 'Money Matters',
      zipCode: '3015 KK',
    },
    '990033': {
      addressExtensionDescription: null,
      city: 'New Orleans',
      country: 'Nederland',
      electronicAddressPrimary: null,
      houseNumber: 100,
      houseNumberExtension: 'A',
      marketRole: 'DDK' as MarketRoleType,
      mprId: '990033',
      registrationNumberOrigin: 'NL',
      statutoryName: 'Volt Power',
      streetName: 'Energy street',
      subjectDescription: 'Verrekenen meetfouten',
      zipCode: '3015 KK',
    },
  };

  const contact = invoiceContacts[request.mprIdOrganization];

  if (!contact) {
    return [{ ...overrides }];
  }

  const subUnitsWithMrId = contact.subUnits?.map((subUnit, index) => ({
    ...subUnit,
    mrId:
      subUnit.mrId ||
      (index === 0
        ? request.mprIdOrganization
        : `${request.mprIdOrganization}-${index}`),
  })) || [{ mrId: request.mprIdOrganization }];

  return [
    {
      ...contact,
      subUnits: subUnitsWithMrId,
      ...overrides,
    },
  ];
};
